import {
  AdvancedFilterModel,
  ColumnAdvancedFilterModel,
  JoinAdvancedFilterModel,
  SortModelItem,
} from 'ag-grid-enterprise';

import { isNonNullable, isNullable } from '@camelot/utils';

type WhereScalar = string | number | boolean | null;
type WhereOperator = {
  [op: string]: WhereScalar | WhereOperator;
};
type WhereType = {
  [index: string]: WhereType[] | WhereOperator;
};

export const graphQlFilterToWhere = (filterModel: AdvancedFilterModel): WhereType | null => {
  if (!filterModel) {
    return null;
  }

  if (filterModel.filterType === 'join') {
    const joinFilter = <JoinAdvancedFilterModel>filterModel;

    if (joinFilter.conditions.length === 0) {
      return null;
    }
    const joinedConditions = joinFilter.conditions.map(graphQlFilterToWhere).filter(isNonNullable);

    return {
      [joinFilter.type.toLocaleLowerCase()]: joinedConditions,
    };
  }

  const getFilter = (filterModel: ColumnAdvancedFilterModel) => {
    if (filterModel.filterType === 'boolean') {
      return filterModel.type === 'true' ? true : false;
    }
    return filterModel.filter;
  };
  const columnFilter = <ColumnAdvancedFilterModel>filterModel;
  const { colId, type } = columnFilter;
  const filter = getFilter(filterModel);

  const operatorMapping: Record<string, string> = {
    equals: 'eq',
    notEqual: 'neq',
    contains: 'contains',
    notContains: 'ncontains',
    startsWith: 'startsWith',
    endsWith: 'endsWith',
    lessThan: 'lt',
    lessThanOrEqual: 'lte',
    greaterThan: 'gt',
    greaterThanOrEqual: 'gte',
    blank: 'eq', // Null pour les colonnes vides
    notBlank: 'neq',
    true: 'eq',
    false: 'eq',
  };

  const graphqlOperator = operatorMapping[type];
  if (!graphqlOperator) {
    return null;
  }

  if (isNullable(filter)) {
    if (type === 'blank') {
      return { [colId]: { [graphqlOperator]: null } };
    } else if (type === 'notBlank') {
      return { [colId]: { [graphqlOperator]: null } };
    } else {
      return null;
    }
  }

  if (columnFilter.filterType === 'number') {
    if (type === 'contains') {
      return { [colId]: { some: { eq: filter } } };
    }
    if (type === 'notContains') {
      return { [colId]: { none: { eq: filter } } };
    }
  }

  return { [colId]: { [graphqlOperator]: filter } };
};

type OrderType = {
  [index: string]: 'ASC' | 'DESC';
};

export const graphQLSortOrder = (sortModel: SortModelItem[]): OrderType[] => {
  if (!sortModel || sortModel.length === 0) {
    return [];
  }

  return sortModel.map(item => ({
    [item.colId]: item.sort === 'asc' ? 'ASC' : 'DESC',
  }));
};
